import type React from 'react';
import {useCallback, useEffect, useRef} from 'react';
import {useAbortSignal} from '@pb/kit-hooks-use-abort-signal';
// @ts-expect-error
import {addStyle} from '@pb/kit-util-add-style';

import {CHANGE_TYPE, SORTABLE_DIRECTION, SORTABLE_POSITION, defaultClassNames, defaultDataIdAttr} from '../constants';
import {shouldSorting} from './utils/shouldSorting';
import {removeTargetList} from './utils/removeTargetList';
import {isOutOfContainer} from './utils/isOutOfContainer';
import {getActiveSide} from './utils/getActiveSide';
import {setActiveClass} from './utils/setActiveClass';

import type {UseDrag, UseDragParams} from '../typedef';


export const useDrag: UseDrag = (
    {
        direction,
        dataIdAttr,
        onApply,
        disabled,
        removable,
        ghostMix,
        refContainer,
        onHover: onMouseOver,
        itemIds,
        setItemIds,
        allowedDragArea,
        onDrop: onDropHandler,
        onDragOver: onDragOverHandler,
        onDragLeave: onDragLeaveHandler,
        onDragStart: onDragStartHandler,
        targetElement: targetElementFromAllowedDragArea,
    }: UseDragParams) => {

    const {createAbortSignal, abort} = useAbortSignal();

    const itemIdsRef = useRef<string[]>(itemIds);
    const targetElement = useRef<HTMLElement | null>(null);
    const dragElement = useRef<HTMLElement | null>(null);
    const offsetDragElement = useRef<{top: number, left: number}>({top: 0, left: 0});
    const draggableId = useRef<string>('');
    const rectContainer = useRef<DOMRect | null>(null);
    const position = useRef<string>(SORTABLE_POSITION.AFTER);
    const isRemove = useRef<boolean>(false);

    useEffect(() => {
        itemIdsRef.current = itemIds;
    }, [itemIds]);

    const onDrag = useCallback((e: DragEvent) => {
        e.preventDefault();

        if (e.clientX === 0 && e.clientY === 0) {
            return;
        }

        const outOfScope = isOutOfContainer({
            cursorCoordinates: {
                x: allowedDragArea ? e.pageX : e.clientX,
                y: allowedDragArea ? e.pageY : e.clientY,
            },
            direction: direction || SORTABLE_DIRECTION.HORIZONTAL,
            rectContainer: rectContainer.current,
            offsetDragElement: offsetDragElement.current,
            allowedDragArea,
        });

        if (outOfScope && dragElement.current && !dragElement.current.classList.contains(defaultClassNames.remove)) {
            dragElement.current.classList.add(defaultClassNames.remove);
            isRemove.current = true;
        } else if (
            !outOfScope &&
            dragElement.current && dragElement.current.classList.contains(defaultClassNames.remove)
        ) {
            dragElement.current.classList.remove(defaultClassNames.remove);
            isRemove.current = false;
        }

        if (dragElement.current && dragElement.current.classList.contains(defaultClassNames.remove)) {
            const {clientX, clientY} = e;
            dragElement.current.style.transform = `translate3d(${clientX}px, ${clientY}px, 0)`;
        }
    }, [direction, allowedDragArea]);

    const onDragEnd = useCallback((event?: DragEvent) => {
        if (event && event.dataTransfer?.dropEffect === 'none' &&
            isOutOfContainer({
                cursorCoordinates: {
                    x: allowedDragArea ? event.pageX : event.clientX,
                    y: allowedDragArea ? event.pageY : event.clientY,
                },
                direction: direction || SORTABLE_DIRECTION.HORIZONTAL,
                rectContainer: rectContainer.current,
                offsetDragElement: offsetDragElement.current,
                allowedDragArea,
            })) {
            isRemove.current = true;
        } else if (!event && targetElementFromAllowedDragArea) {
            isRemove.current = false;
        }

        let sortedIds = itemIdsRef.current;

        if (dragElement.current) {
            dragElement.current.remove();
            dragElement.current = null;
        }

        if (refContainer.current) {
            const target = targetElement.current;
            const draggableList = refContainer.current.querySelectorAll(`.${defaultClassNames.draggable}`);
            const ids = [...draggableList].map(element =>
                (element as HTMLElement).dataset[dataIdAttr || defaultDataIdAttr]);
            const targetId = targetElementFromAllowedDragArea ?
                targetElementFromAllowedDragArea.dataId : target && target.dataset[dataIdAttr || defaultDataIdAttr];
            const sortablePosition = targetElementFromAllowedDragArea ?
                targetElementFromAllowedDragArea.sortablePosition : position.current;

            if (targetId) {
                sortedIds = [...draggableList].reduce((prev, curr) => {
                    const currentId = (curr as HTMLElement).dataset[dataIdAttr || defaultDataIdAttr] || '';

                    if (currentId === targetId) {
                        if (sortablePosition === SORTABLE_POSITION.AFTER) {
                            return [...prev, targetId, draggableId.current];
                        }

                        return target || targetElementFromAllowedDragArea
                            ? [...prev, draggableId.current, targetId]
                            : prev;
                    }

                    return currentId === draggableId.current
                        ? prev
                        : [...prev, (curr as HTMLElement).dataset[dataIdAttr || defaultDataIdAttr] || ''];
                }, [] as string[]);
            } else {
                sortedIds = ids.filter(id => id && id !== draggableId.current) as string[];
            }

            if (onApply) {
                const draggableIndex = ids.indexOf(draggableId.current);
                const targetIndex = typeof targetId === 'string' ? ids.indexOf(targetId) : -1;
                const shouldUpdate = shouldSorting(draggableIndex, targetIndex, sortablePosition);

                if (shouldUpdate || isRemove.current) {
                    onApply({
                        draggable: {
                            id: draggableId.current,
                            newIndex: sortedIds.indexOf(draggableId.current),
                            oldIndex: draggableIndex,
                        },
                        target: {
                            id: targetId || null,
                            newIndex: typeof targetId === 'string' ? sortedIds.indexOf(targetId) : -1,
                            oldIndex: targetIndex,
                        },
                        itemIds: sortedIds,
                        type: isRemove.current ? CHANGE_TYPE.REMOVE : CHANGE_TYPE.UPDATE,
                    });
                }
            }

            setItemIds(sortedIds);
            isRemove.current = false;
        }

        removeTargetList(targetElement.current);
    }, [
        allowedDragArea,
        dataIdAttr,
        direction,
        onApply,
        refContainer,
        setItemIds,
        targetElementFromAllowedDragArea,
    ]);

    useEffect(() => {
        if (targetElementFromAllowedDragArea) {
            onDragEnd();
        }
    }, [onDragEnd, targetElementFromAllowedDragArea]);

    const onMouseDown = useCallback((e: React.MouseEvent<HTMLElement>) => {
        if (!disabled) {
            const target = e.target as HTMLElement;

            if (target) {
                const draggableElement = target.closest(`.${defaultClassNames.draggable}`) as HTMLElement;

                if (draggableElement) {
                    abort();
                    const signal = createAbortSignal();
                    draggableElement.addEventListener('dragend', onDragEnd, {signal});
                    draggableElement.addEventListener('drag', onDrag, {signal});
                    draggableId.current = draggableElement.dataset[dataIdAttr || defaultDataIdAttr] || '';
                    draggableElement.draggable = true;

                    if (removable) {
                        const {width, height, top, left} = draggableElement.getBoundingClientRect();

                        dragElement.current = document.createElement('div');
                        addStyle(dragElement.current, {
                            position: 'fixed',
                            pointerEvents: 'none',
                            top: `${(height / 2) - (e.clientY - top)}px`,
                            left: `${(width / 2) - (e.clientX - left)}px`,
                        });

                        if (document.body && dragElement.current) {
                            document.body.appendChild(dragElement.current);
                        }
                    }
                }
            }
        }
    }, [abort, createAbortSignal, dataIdAttr, disabled, onDrag, onDragEnd, removable]);

    const onMouseUp = () => {
        abort();
        if (dragElement.current) {
            dragElement.current.remove();
            dragElement.current = null;
        }
    };

    const onDragStart = useCallback((e: React.DragEvent<HTMLElement>) => {
        const target = e.target as HTMLElement;
        target.draggable = false;
        target.classList.add(defaultClassNames.ghost);

        offsetDragElement.current = {top: e.clientY, left: e.clientX};
        rectContainer.current = refContainer.current && refContainer.current.getBoundingClientRect();

        if (ghostMix) {
            target.classList.add(ghostMix);
        }

        setTimeout(() => {
            target.classList.remove(defaultClassNames.ghost);

            if (ghostMix) {
                target.classList.remove(ghostMix);
            }
        }, 0);

        if (onDragStartHandler) {
            onDragStartHandler(e);
        }
    }, [ghostMix, onDragStartHandler, refContainer]);


    const onDragEnter = useCallback((e: React.DragEvent<HTMLElement>) => {
        const target = e.target as HTMLElement;

        if (target) {
            const siblingElement = target.closest(`.${defaultClassNames.draggable}`) as HTMLElement;

            if (targetElement.current !== siblingElement) {
                removeTargetList(targetElement.current);
                targetElement.current = siblingElement;
            }

            if (!direction && !siblingElement.classList.contains(defaultClassNames.targetClassName)) {
                siblingElement.classList.add(defaultClassNames.targetClassName);
            }
        }
    }, [direction]);

    const onHover = useCallback(() => {
        if (targetElement.current) {
            const draggableIndex = itemIds.indexOf(draggableId.current);
            const targetIndex =
                itemIds.indexOf(targetElement.current.dataset[dataIdAttr || defaultDataIdAttr] || '');
            const shouldUpdate = shouldSorting(draggableIndex, targetIndex, position.current);

            if (onMouseOver && shouldUpdate) {
                onMouseOver(true, targetIndex, position.current);
            } else if (onMouseOver) {
                onMouseOver(false, targetIndex, position.current);
            }
        }
    }, [dataIdAttr, itemIds, onMouseOver]);

    const onDragOver = useCallback((e: React.DragEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (direction && targetElement.current) {
            const {current} = targetElement;
            const {width, height, top, left} = current.getBoundingClientRect();
            const positionX = e.clientX - left;
            const positionY = e.clientY - top;

            if (direction === SORTABLE_DIRECTION.HORIZONTAL) {
                position.current = getActiveSide({
                    size: width,
                    cursorPosition: positionX,
                }) || position.current;
            } else if (direction === SORTABLE_DIRECTION.VERTICAL) {
                position.current = getActiveSide({
                    size: height,
                    cursorPosition: positionY,
                }) || position.current;
            }

            setActiveClass({
                direction,
                position: position.current,
                onHover,
                targetElement: targetElement.current,
            });
        }

        onDragOverHandler?.(e);
    }, [direction, onDragOverHandler, onHover]);


    const onDragLeave = useCallback((e: React.DragEvent<HTMLElement>) => {
        e.preventDefault();

        if (e.target === targetElement.current) {
            onHover();
            removeTargetList(targetElement.current);
            targetElement.current = null;

            if (dragElement.current && onDragLeaveHandler) {
                onDragLeaveHandler(e);
            }
        }
    }, [onDragLeaveHandler, onHover]);

    const onDrop = useCallback((e: React.DragEvent<HTMLElement>) => {
        e.preventDefault();
        onDropHandler?.(e);
    }, [onDropHandler]);

    return {
        onMouseDown,
        onMouseUp,
        onDragStart,
        onDragEnter,
        onDragOver,
        onDragLeave,
        onDrop,
    };
};
